import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { IncidentApprovalTransition, IncidentCommunicationTransition, IncidentNotificationState } from "@no-busy-work/ops-bridge-protocol";
import { AppShellHeader, Button, LinkButton, Popover } from "@no-busy-work/ui";
import {
  BUTTON_TYPE,
  GRAPH_HEALTH,
  LOCALE_OPTIONS,
  OUTAGE_NOTICE_STATE,
  OPS_ACTION_ENDPOINT_KIND,
  TOOL_STATE,
  UI_TONE,
  WORKFLOW_CLEAR_STATES,
  WORKFLOW_STATUS
} from "../OpsCenter.Config";
import { incidentCreateCatalog, optionsForApp, ownerDisplayOptions, type CatalogOptionTuple } from "../OpsCenter.Catalog";
import {
  actionRows,
  aiGuardrailRows,
  aiContextRows,
  aiPanelFollowUps,
  aiSuggestedQuestions,
  actorRefLabel,
  communicationEvidenceRows,
  communicationSummaryRows,
  customerImpactBandRows,
  formatTime,
  graphFocusRows,
  impactGraph,
  capabilityProposalRows,
  learningRows,
  notificationRows,
  notificationTone,
  resolutionRows,
  resolutionNotificationRows,
  routeLabel,
  serviceLabel,
  severityTone,
  slaLabel,
  slaSeconds,
  statusTone,
  toolRows
} from "../OpsCenter.Data";
import { TAB_ID } from "../OpsCenter.Types";
import type { ActionRow, GraphEdge, IncidentDetail, LocaleCode, OpsCenterActions, OpsCenterCopy, OpsCenterState, TabId, UiTone } from "../OpsCenter.Types";
import { humanize, localizedEvidenceText, resolveLocale, statusLabel } from "../i18n/OpsCenter.I18n";
import {
  authProviderLabel,
  isMicrosoftEntraSession,
  isOperatorAccessCodeSession,
  previewFixtureMode
} from "../hooks/useOpsCenterState";
import { ActiveTaskWorkspace, IncidentCommandHeader, IncidentLifecycleStrip, IncidentSummaryGrid, ResponderStrip } from "./OpsCenterScreen.CommandSurface";
import { CustomerImpactDrilldown } from "./OpsCenterScreen.CustomerImpact";
import { IncidentRail } from "./OpsCenterScreen.IncidentRail";
import { ToolInventoryDrawer } from "./OpsCenterScreen.ToolInventory";
import { ResolutionProgress } from "./OpsCenterScreen.ResolutionProgress";
import { Messages, Metric, SectionHeader, SelectField, StatusPill, TextField, formatLabel, submit } from "./OpsCenterScreen.Shared";

interface OpsSectionProps {
  readonly state: OpsCenterState;
  readonly actions: OpsCenterActions;
  readonly copy: OpsCenterCopy;
  readonly locale: LocaleCode;
  readonly onLocaleChange?: (locale: LocaleCode) => void;
}

interface IncidentSectionProps extends OpsSectionProps {
  readonly detail: IncidentDetail;
}

const ARIA_HIDDEN = "true";
const CUSTOMER_DRILLDOWN_LIMIT = 25;
const INCIDENT_VIEW_ANCHOR_ID = "ops-incident-views";
const INCIDENT_VIEW_PANEL_ID = "ops-incident-view-panel";
const MOBILE_DIRECT_TABS: readonly TabId[] = [TAB_ID.Overview, TAB_ID.Impact, TAB_ID.Comms, TAB_ID.Resolution];
const OPS_MOBILE_TAB_ICON_CLASS = "ops-mobile-tab-icon";

export function AuthGate({ state, actions, copy }: OpsSectionProps): ReactNode {
  const isBooting = state.busy && state.session === null;
  return (
    <main className="auth-shell">
      <section className="auth-shell-panel-wrap">
        <div className="auth-panel">
          <div className="brand-mark" aria-hidden={ARIA_HIDDEN}>O</div>
          <p className="eyebrow">{copy.auth.appName}</p>
          <h1>{copy.auth.authTitle}</h1>
          <p className="muted-copy">{copy.auth.authBody}</p>
          {isOperatorAccessCodeSession(state.session) ? (
            <form className="login-form" onSubmit={(event) => submit(event, actions.loginWithOperatorAccessCode)}>
              <TextField
                autoComplete="current-password"
                label={copy.auth.accessCodeLabel}
                name="operatorAccessCode"
                type="password"
                value={state.operatorAccessCode}
                onChange={actions.setOperatorAccessCode}
              />
              <Button className="auth-primary-button" disabled={state.busy} isLoading={state.busy} loadingLabel={copy.auth.working} type={BUTTON_TYPE.Submit} variant="primary">
                {state.busy ? copy.auth.working : copy.auth.signIn}
              </Button>
            </form>
          ) : state.session?.provider !== "disabled" && state.session?.loginUrl ? (
            <div className="auth-actions">
              <LinkButton className="auth-primary-button auth-link-button" href={buildLoginHref(state.session.loginUrl)} variant="primary">
                {copy.auth.signIn}
              </LinkButton>
            </div>
          ) : isBooting ? (
            <p className="muted-copy">{copy.auth.working}</p>
          ) : (
            <p className="auth-error">{authProviderLabel(state, copy)}</p>
          )}
          {isBooting ? null : <Messages state={state} />}
        </div>
      </section>
    </main>
  );
}

export function OpsHeader(props: OpsSectionProps): ReactNode {
  const { state, actions, copy } = props;
  return (
    <AppShellHeader
      activeItemId="ops"
      brandAriaLabel={`${copy.auth.appName} ${copy.auth.eyebrow}`}
      brandContent={
        <span className="ops-app-shell-brand" aria-label={`${copy.auth.appName} ${copy.auth.eyebrow}`}>
          <span className="ops-app-shell-mark" aria-hidden={true}>NBW</span>
          <span>{copy.auth.appName}</span>
          <span className="ops-header-mode">{copy.auth.eyebrow}</span>
        </span>
      }
      className="ops-header"
      items={[]}
      trailingContent={<AccountMenu state={state} actions={actions} copy={copy} locale={props.locale} onLocaleChange={props.onLocaleChange} />}
    />
  );
}

function AccountMenu({ state, actions, copy, locale, onLocaleChange }: Pick<OpsSectionProps, "state" | "actions" | "copy" | "locale" | "onLocaleChange">): ReactNode {
  const displayName = state.session?.authenticated ? state.session.actor.displayName : copy.auth.signedOut;
  const initials = resolveActorInitials(displayName);
  return (
    <Popover
      triggerAriaLabel={displayName}
      triggerIcon={
        <span className="ops-account-trigger" title={displayName}>
          <span className="ops-account-avatar" aria-hidden={ARIA_HIDDEN}>{initials}</span>
          <span className="ops-account-chevron" aria-hidden={ARIA_HIDDEN}>v</span>
        </span>
      }
      content={
      <section className="ops-account-panel">
        <div className="ops-account-identity">
          <span className="ops-account-avatar" aria-hidden={ARIA_HIDDEN}>{initials}</span>
          <strong>{displayName}</strong>
        </div>
        <SelectField label={copy.forms.locale} name="opsLocale" options={LOCALE_OPTIONS} value={locale} onChange={(value) => onLocaleChange?.(resolveLocale(value))} />
        <Button className="ghost-button" type={BUTTON_TYPE.Button} variant="ghost" onClick={actions.openSettings}>
          {copy.actions.openSettings}
        </Button>
        <Button className="ghost-button" type={BUTTON_TYPE.Button} variant="ghost" onClick={() => void actions.logout()}>
          {copy.auth.signOut}
        </Button>
      </section>
      }
    />
  );
}

export function OpsCommandBar({ state, actions, copy }: OpsSectionProps): ReactNode {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const tabs = Object.keys(copy.tabs) as TabId[];
  const directTabs = MOBILE_DIRECT_TABS.filter((tab) => tabs.includes(tab));
  const overflowTabs = tabs.filter((tab) => !directTabs.includes(tab));
  const overflowActive = overflowTabs.includes(state.activeTab);
  const activateTab = (tab: TabId) => {
    setMobileMenuOpen(false);
    actions.setActiveTab(tab);
  };
  return (
    <section className="ops-command-bar" aria-label={copy.sections.workspace}>
      <nav className="ops-primary-tabs" role="tablist" aria-label={copy.sections.incidentViews}>
        {tabs.map((tab) => (
          <button
            aria-selected={tab === state.activeTab}
            className={tab === state.activeTab ? "ops-primary-tab active" : "ops-primary-tab"}
            key={tab}
            role="tab"
            aria-controls={INCIDENT_VIEW_PANEL_ID}
            type={BUTTON_TYPE.Button}
            onClick={() => activateTab(tab)}
          >
            {copy.tabs[tab]}
          </button>
        ))}
      </nav>
      <nav className="ops-mobile-tab-menu" aria-label={copy.sections.incidentViews}>
        {mobileMenuOpen ? <button aria-label={copy.actions.close} className="ops-mobile-tab-backdrop" type={BUTTON_TYPE.Button} onClick={() => setMobileMenuOpen(false)} /> : null}
        {mobileMenuOpen ? (
          <div className="ops-mobile-tab-panel" role="tablist" aria-label={copy.sections.incidentViews}>
            {overflowTabs.map((tab) => (
              <button
                aria-selected={tab === state.activeTab}
                className={tab === state.activeTab ? "ops-mobile-tab-option active" : "ops-mobile-tab-option"}
                data-mobile-tab={tab}
                key={tab}
                role="tab"
                aria-controls={INCIDENT_VIEW_PANEL_ID}
                type={BUTTON_TYPE.Button}
                onClick={() => activateTab(tab)}
              >
                {copy.tabs[tab]}
              </button>
            ))}
          </div>
        ) : null}
        <div className="ops-mobile-tab-bar" role="tablist" aria-label={copy.sections.incidentViews}>
          {directTabs.map((tab) => (
            <button
              aria-selected={tab === state.activeTab}
              className={tab === state.activeTab ? "ops-mobile-tab-button active" : "ops-mobile-tab-button"}
              data-mobile-tab={tab}
              key={tab}
              role="tab"
              aria-controls={INCIDENT_VIEW_PANEL_ID}
              type={BUTTON_TYPE.Button}
              onClick={() => activateTab(tab)}
            >
              <MobileTabIcon tab={tab} />
              <span>{copy.tabs[tab]}</span>
            </button>
          ))}
          <button
            aria-expanded={mobileMenuOpen}
            aria-haspopup="menu"
            className={mobileMenuOpen || overflowActive ? "ops-mobile-tab-button ops-mobile-more-button active" : "ops-mobile-tab-button ops-mobile-more-button"}
            type={BUTTON_TYPE.Button}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            <MobileTabIcon tab="more" />
            <span>{copy.actions.more}</span>
          </button>
        </div>
      </nav>
    </section>
  );
}

function MobileTabIcon({ tab }: { readonly tab: TabId | "more" }): ReactNode {
  if (tab === TAB_ID.Impact) {
    return (
      <svg className={OPS_MOBILE_TAB_ICON_CLASS} aria-hidden={ARIA_HIDDEN} viewBox="0 0 24 24">
        <circle cx="6" cy="12" r="2.5" />
        <circle cx="18" cy="7" r="2.5" />
        <circle cx="18" cy="17" r="2.5" />
        <path d="M8.5 12 15.5 8" />
        <path d="M8.5 12 15.5 16" />
      </svg>
    );
  }
  if (tab === TAB_ID.Comms) {
    return (
      <svg className={OPS_MOBILE_TAB_ICON_CLASS} aria-hidden={ARIA_HIDDEN} viewBox="0 0 24 24">
        <path d="M5 7h14v9H9l-4 3V7Z" />
        <path d="M8 10h8" />
        <path d="M8 13h5" />
      </svg>
    );
  }
  if (tab === TAB_ID.Resolution) {
    return (
      <svg className={OPS_MOBILE_TAB_ICON_CLASS} aria-hidden={ARIA_HIDDEN} viewBox="0 0 24 24">
        <path d="m5 13 4 4L19 7" />
        <path d="M4 20h16" />
      </svg>
    );
  }
  if (tab === "more") {
    return (
      <svg className={OPS_MOBILE_TAB_ICON_CLASS} aria-hidden={ARIA_HIDDEN} viewBox="0 0 24 24">
        <circle cx="6" cy="12" r="1.75" />
        <circle cx="12" cy="12" r="1.75" />
        <circle cx="18" cy="12" r="1.75" />
      </svg>
    );
  }
  return (
    <svg className={OPS_MOBILE_TAB_ICON_CLASS} aria-hidden={ARIA_HIDDEN} viewBox="0 0 24 24">
      <path d="M5 6h14" />
      <path d="M5 12h14" />
      <path d="M5 18h14" />
    </svg>
  );
}

export function OpsWorkspace(props: OpsSectionProps): ReactNode {
  return (
    <div className="ops-cockpit">
      <IncidentRail {...props} />
      <CenterPanel {...props} />
      <AttentionRail {...props} />
    </div>
  );
}

export function AiDrawer({ state, actions, copy }: OpsSectionProps): ReactNode {
  if (!state.aiOpen) {
    return (
      <button
        aria-expanded={state.aiOpen}
        aria-label={copy.actions.openAi}
        className="ai-launcher"
        title={copy.actions.openAi}
        type={BUTTON_TYPE.Button}
        onClick={actions.openAi}
      >
        <BotGlyph />
      </button>
    );
  }
  const suggestions = aiSuggestedQuestions(state.incident, copy);
  const followUps = aiPanelFollowUps(state.incident, copy);
  const guardrails = aiGuardrailRows(copy);
  return (
    <aside className="ai-drawer open" aria-label={copy.orchestrator.title}>
      <form
        className="ai-panel"
        onSubmit={(event) => {
          event.preventDefault();
          actions.askAiQuestion();
        }}
      >
        <div className="ai-drawer-top">
          <div className="ai-title-row">
            <BotGlyph />
            <h2>{copy.orchestrator.title}</h2>
          </div>
          <button aria-label={copy.actions.close} className="ai-close-button" title={copy.actions.close} type={BUTTON_TYPE.Button} onClick={actions.closeAi}>
            X
          </button>
        </div>
        <p className="muted-copy">{orchestratorSummary(state.incident, copy)}</p>
        <section className="ai-context-section" aria-label={copy.sections.currentContext}>
          <SectionHeader title={copy.sections.currentContext} status={state.incident ? copy.sections.active : copy.status.pending} />
          <ListRows rows={aiContextRows(state.incident, copy)} />
        </section>
        <section className="ai-context-section" aria-label={copy.orchestrator.suggestionsLabel}>
          <SectionHeader title={copy.orchestrator.suggestionsLabel} status={String(suggestions.length)} />
          <div className="ai-suggestion-grid">
            {suggestions.map((suggestion) => (
              <button
                className={`ai-suggestion-button ${suggestion.tone}`}
                key={suggestion.kind}
                type={BUTTON_TYPE.Button}
                onClick={() => actions.askAiQuestion(suggestion.label, suggestion.kind)}
              >
                <strong>{suggestion.label}</strong>
                <span>{suggestion.summary}</span>
              </button>
            ))}
          </div>
        </section>
        <section className="ai-context-section" aria-label={copy.orchestrator.followUpsLabel}>
          <SectionHeader title={copy.orchestrator.followUpsLabel} status={String(followUps.length)} />
          <div className="ai-follow-up-list panel-follow-ups">
            {followUps.map((followUp) => (
              <button
                className={`ai-follow-up-card ${followUp.tone}`}
                key={followUp.id}
                type={BUTTON_TYPE.Button}
                onClick={() => actions.askAiQuestion(followUp.label, followUp.kind)}
              >
                <strong>{followUp.label}</strong>
                <span>{followUp.summary}</span>
              </button>
            ))}
          </div>
        </section>
        <section className="ai-context-section" aria-label={copy.orchestrator.guardrailsLabel}>
          <SectionHeader title={copy.orchestrator.guardrailsLabel} status={copy.status.guarded} tone={UI_TONE.Critical} />
          <div className="ai-guardrail-list">
            {guardrails.map(([label, summary, tone]) => (
              <article className={`ai-guardrail-card ${tone}`} key={label}>
                <strong>{label}</strong>
                <span>{summary}</span>
              </article>
            ))}
          </div>
        </section>
        <section className="ai-compose-section" aria-label={copy.orchestrator.questionLabel}>
          <label className="field">
            <span>{copy.orchestrator.questionLabel}</span>
            <textarea
              name="aiQuestion"
              placeholder={copy.orchestrator.placeholder}
              rows={3}
              value={state.aiQuestionDraft}
              onChange={(event) => actions.setAiQuestionDraft(event.target.value)}
            />
          </label>
          <button className="primary-button" disabled={!state.aiQuestionDraft.trim()} type={BUTTON_TYPE.Submit}>
            {copy.orchestrator.submit}
          </button>
          <p className="subtle-copy">{copy.orchestrator.localBoundary}</p>
        </section>
        <section className="ai-thread-list" aria-label={copy.orchestrator.answersLabel}>
          {state.aiThreads.length === 0 ? (
            <p className="subtle-copy">{copy.orchestrator.empty}</p>
          ) : (
            state.aiThreads.map((thread) => (
              <article className="ai-thread-card" key={thread.id}>
                <strong>{thread.question}</strong>
                <p>{thread.answer}</p>
                <div className="ai-follow-up-list" aria-label={copy.orchestrator.followUpsLabel}>
                  {thread.followUps.map((followUp) => (
                    <button className="ai-follow-up-button" key={`${thread.id}:${followUp}`} type={BUTTON_TYPE.Button} onClick={() => actions.askAiQuestion(followUp)}>
                      {followUp}
                    </button>
                  ))}
                </div>
              </article>
            ))
          )}
        </section>
      </form>
    </aside>
  );
}

export function OpsSettingsDrawer({ state, actions, copy }: OpsSectionProps): ReactNode {
  if (!state.settingsOpen) {
    return null;
  }
  const catalog = incidentCreateCatalog(state.health);
  return (
    <aside className="settings-drawer open" aria-label={copy.sections.settings}>
      <section className="settings-panel">
        <div className="settings-drawer-top">
          <h2>{copy.sections.settings}</h2>
          <button aria-label={copy.actions.close} className="ai-close-button" title={copy.actions.close} type={BUTTON_TYPE.Button} onClick={actions.closeSettings}>
            X
          </button>
        </div>
        <ListPanel title={copy.sections.appEnvironments} rows={appEnvironmentRows(catalog.apps, catalog.environmentsByApp, copy)} />
        <ListPanel title={copy.sections.ownerDirectory} rows={ownerCatalogRows(catalog.owners, copy)} />
        <ListPanel title={copy.sections.audienceDirectory} rows={optionValueRows(catalog.audiences)} />
        <ListPanel title={copy.sections.aiAssignmentContext} rows={aiAssignmentContextRows(copy)} />
      </section>
    </aside>
  );
}

function CenterPanel(props: OpsSectionProps): ReactNode {
  const { state } = props;
  return (
    <section className="center-panel">
      {state.incident ? <ActiveIncidentPanel {...props} detail={state.incident} /> : <StandbyPanel {...props} />}
    </section>
  );
}

function ActiveIncidentPanel(props: IncidentSectionProps): ReactNode {
  const { detail, state, actions, copy } = props;
  return (
    <>
      <section className="incident-command-surface" aria-label={copy.labels.currentIncident}>
        <IncidentCommandHeader copy={copy} detail={detail} />
      </section>
      <ResolutionProgress state={state} actions={actions} copy={copy} detail={detail} />
      <ActiveTaskWorkspace actions={actions} busy={state.busy} copy={copy} detail={detail} rootCause={state.rootCause} />
      <ResponderStrip copy={copy} detail={detail} rootCause={state.rootCause} />
      <section className="incident-command-surface command-support-stack" aria-label={copy.sections.workspace}>
        <IncidentSummaryGrid actions={actions} busy={state.busy} copy={copy} detail={detail} />
      </section>
      <WorkflowSupportSummaryGrid state={state} actions={actions} copy={copy} detail={detail} locale={props.locale} />
      <IncidentLifecycleStrip copy={copy} detail={detail} />
      <div className="incident-view-anchor" id={INCIDENT_VIEW_ANCHOR_ID}>
        <OpsCommandBar state={state} actions={actions} copy={copy} locale={props.locale} />
        <div className="incident-view-panel" id={INCIDENT_VIEW_PANEL_ID} role="tabpanel" aria-label={copy.tabs[state.activeTab]}>
          <TabContent {...props} />
        </div>
      </div>
    </>
  );
}

function WorkflowSupportSummaryGrid(props: IncidentSectionProps): ReactNode {
  const { detail, state, actions, copy } = props;
  const tools = toolRows(detail, Boolean(state.health?.ok), state.health?.toolCatalog ?? []);
  const involved = tools.filter((tool) => tool.state === TOOL_STATE.Involved);
  const approvals = detail.decisions.length + detail.ownerEscalations.length;
  const actionItems = attentionActionRows(detail, copy);
  const codeBridgeCount = codeBridgeActionRows(detail, copy).length;
  const recoveryAttentionCount = approvals + actionItems.length + codeBridgeCount;
  const latestTimeline = detail.timeline.slice(-1)[0];
  const graph = impactGraph(detail, copy);
  const activeEdge = graph.edges.find((edge) => edge.health !== GRAPH_HEALTH.Healthy) ?? graph.edges[0];
  const summary = detail.communicationSummary;
  return (
    <section className="workflow-support-grid" aria-label={copy.sections.workspace}>
      <SupportSummaryCard
        action={(
          <button className="secondary-button compact-action" type={BUTTON_TYPE.Button} onClick={() => actions.setActiveTab(TAB_ID.Comms)}>
            {copy.tabs[TAB_ID.Comms]}
          </button>
        )}
        status={`${summary.impactedCustomerCount} ${copy.labels.impacted}`}
        title={copy.sections.customerImpactComms}
      >
        <p className="support-summary-copy">
          {copy.labels.outageNotice}: {statusLabel(copy, summary.outageNoticeState)} / {copy.labels.resolution}: {statusLabel(copy, summary.resolutionNotificationState)}
        </p>
        <dl className="support-summary-stats">
          <div>
            <dt>{copy.labels.complaints}</dt>
            <dd>{summary.complaintCount}</dd>
          </div>
          <div>
            <dt>{copy.labels.deliveryRisks}</dt>
            <dd>{summary.deliveryRiskCount}</dd>
          </div>
          <div>
            <dt>{copy.labels.currentBlocker}</dt>
            <dd>{currentBlocker(detail, copy)}</dd>
          </div>
        </dl>
        <div className="support-summary-pills">
          {customerImpactBandRows(detail, copy).slice(0, 4).map((row) => (
            <StatusPill key={row.label} label={row.label} tone={row.tone} />
          ))}
        </div>
      </SupportSummaryCard>
      <SupportSummaryCard
        action={(
          <button className="secondary-button compact-action" type={BUTTON_TYPE.Button} onClick={() => actions.setActiveTab(TAB_ID.Actions)}>
            {copy.tabs[TAB_ID.Actions]}
          </button>
        )}
        status={recoveryAttentionCount ? `${recoveryAttentionCount}` : copy.sections.clear}
        title={copy.sections.actionRail}
      >
        <p className="support-summary-copy">
          {actionItems[0]?.summary ?? (codeBridgeCount ? copy.labels.permanentFixRisk : copy.labels.noActiveBlockers)}
        </p>
        <dl className="support-summary-stats">
          <div>
            <dt>{copy.sections.approvalQueue}</dt>
            <dd>{approvals}</dd>
          </div>
          <div>
            <dt>{copy.sections.actions}</dt>
            <dd>{actionItems.length}</dd>
          </div>
          <div>
            <dt>{copy.sections.codeBridge}</dt>
            <dd>{codeBridgeCount}</dd>
          </div>
        </dl>
      </SupportSummaryCard>
      <SupportSummaryCard
        action={(
          <button className="secondary-button compact-action" type={BUTTON_TYPE.Button} onClick={() => actions.setActiveTab(TAB_ID.Impact)}>
            {copy.tabs[TAB_ID.Impact]}
          </button>
        )}
        status={`${graph.edges.length} ${copy.labels.routes}`}
        title={copy.sections.impactGraph}
      >
        <p className="support-summary-copy">
          {activeEdge ? activeEdge.summary : serviceLabel(detail.incident.serviceId, copy)}
        </p>
        <dl className="support-summary-stats">
          <div>
            <dt>{copy.labels.service}</dt>
            <dd>{serviceLabel(detail.incident.serviceId, copy)}</dd>
          </div>
          <div>
            <dt>{copy.labels.routes}</dt>
            <dd>{uniqueCustomerValues(detail.customerImpact.map((item) => routeLabel(item.affectedRouteRef, copy))).length}</dd>
          </div>
          <div>
            <dt>{copy.labels.impacted}</dt>
            <dd>{summary.impactedCustomerCount}</dd>
          </div>
        </dl>
      </SupportSummaryCard>
      <SupportSummaryCard
        action={(
          <button className="secondary-button compact-action" type={BUTTON_TYPE.Button} onClick={actions.openToolDrawer}>
            {copy.tools.open}
          </button>
        )}
        status={`${involved.length}/${tools.length}`}
        title={copy.tools.involvedTitle}
      >
        <p className="support-summary-copy">
          {involved.length
            ? involved.slice(0, 3).map((tool) => toolLabel(copy, tool.id, tool.label)).join(" / ")
            : copy.tools.noInvolved}
        </p>
        <dl className="support-summary-stats">
          <div>
            <dt>{copy.tools.readiness}</dt>
            <dd>{tools.filter((tool) => tool.ready).length}</dd>
          </div>
          <div>
            <dt>{copy.tools.approval}</dt>
            <dd>{tools.filter((tool) => tool.requiresApproval).length}</dd>
          </div>
          <div>
            <dt>{copy.sections.timeline}</dt>
                  <dd>{latestTimeline ? statusLabel(copy, latestTimeline.kind) : copy.status.pending}</dd>
          </div>
        </dl>
      </SupportSummaryCard>
    </section>
  );
}

function SupportSummaryCard({
  title,
  status,
  action,
  children
}: {
  readonly title: string;
  readonly status: string;
  readonly action?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section className="support-summary-card">
      <SectionHeader title={title} status={status} />
      <div className="support-summary-body">{children}</div>
      {action ? <div className="support-summary-actions">{action}</div> : null}
    </section>
  );
}

function TabContent(props: IncidentSectionProps): ReactNode {
  const { state, detail, copy } = props;
  if (state.activeTab === TAB_ID.Impact) {
    return <ImpactPanel {...props} />;
  }
  if (state.activeTab === TAB_ID.Timeline) {
    return <TimelinePanel detail={detail} copy={copy} />;
  }
  if (state.activeTab === TAB_ID.Actions) {
    return <ActionsPanel {...props} />;
  }
  if (state.activeTab === TAB_ID.Comms) {
    return <CommsPanel {...props} />;
  }
  if (state.activeTab === TAB_ID.Evidence) {
    return <EvidencePanel {...props} />;
  }
  if (state.activeTab === TAB_ID.Resolution) {
    return <ResolutionPanel {...props} />;
  }
  return <OverviewPanel {...props} />;
}

function OverviewPanel(props: IncidentSectionProps): ReactNode {
  const { detail, copy } = props;
  return (
    <div className="tab-panel">
      <dl className="metric-grid">
        <Metric label={copy.labels.runbook} value={detail.incident.runbook?.title ?? copy.labels.manualTriage} />
        <Metric label={copy.labels.customerImpact} value={`${detail.communicationSummary.impactedCustomerCount} ${copy.labels.impacted}`} />
        <Metric label={copy.labels.complaints} value={String(detail.communicationSummary.complaintCount)} />
        <Metric label={copy.labels.confidence} value={humanize(detail.incident.confidence)} />
        <Metric label={copy.labels.sla} value={slaLabel(detail, copy)} />
      </dl>
      <ImpactGraph {...props} />
      <ListPanel
        title={copy.sections.timeline}
            rows={detail.timeline.slice(-8).reverse().map((item) => [statusLabel(copy, item.kind), localizedEvidenceText(copy, item.redactedBody ?? item.summary)] as const)}
      />
    </div>
  );
}

function ImpactPanel(props: IncidentSectionProps): ReactNode {
  const { detail, copy } = props;
  const routeRefs = uniqueCustomerValues(detail.customerImpact.map((item) => routeLabel(item.affectedRouteRef, copy)));
  const ownerRefs = uniqueCustomerValues([
    ...detail.customerImpact.map((item) => item.ownerRef.replace("owner:", "")),
    ...detail.ownerEscalations.map((item) => item.recipientRef.replace("owner:", ""))
  ]);
  return (
    <div className="tab-panel">
      <ImpactGraph {...props} />
      <dl className="metric-grid">
        <Metric label={copy.labels.products} value={serviceLabel(detail.incident.serviceId, copy)} />
        <Metric label={copy.labels.routes} value={summarizeMetricList(routeRefs, copy)} />
        <Metric label={copy.labels.owners} value={summarizeMetricList(ownerRefs, copy)} />
        <Metric label={copy.labels.audiences} value={`${detail.communicationSummary.impactedCustomerCount} ${copy.labels.impacted}`} />
      </dl>
      <CustomerImpactDrilldown detail={detail} copy={copy} />
    </div>
  );
}

function customerVisibleLabel(copy: OpsCenterCopy, shown: number, total: number): string {
  return formatLabel(copy.labels.showingCustomers, {
    shown: String(shown),
    total: String(total)
  });
}

function uniqueCustomerValues(values: readonly string[]): readonly string[] {
  return [...new Set(values.filter(Boolean))];
}

function summarizeMetricList(values: readonly string[], copy: OpsCenterCopy): string {
  if (!values.length) {
    return copy.labels.pending;
  }
  if (values.length <= 2) {
    return values.join(", ");
  }
  return `${values.slice(0, 2).join(", ")} +${values.length - 2}`;
}


function TimelinePanel({ detail, copy }: { readonly detail: IncidentDetail; readonly copy: OpsCenterCopy }): ReactNode {
  return (
    <ListPanel
      title={copy.sections.timeline}
      rows={detail.timeline.slice().reverse().map((item) => [statusLabel(copy, item.kind), localizedEvidenceText(copy, item.redactedBody ?? item.summary)] as const)}
    />
  );
}

function EvidencePanel({ detail, copy }: IncidentSectionProps): ReactNode {
  return (
    <div className="tab-panel">
      <ListPanel title={copy.sections.evidence} rows={detail.evidence.map((item) => [localizedEvidenceText(copy, item.label), localizedEvidenceText(copy, item.redactedBody)] as const)} />
    </div>
  );
}

function ActionsPanel(props: IncidentSectionProps): ReactNode {
  const { state, detail, actions, copy, locale } = props;
  const rows = actionRows(detail, copy);
  const hasOpenAction = rows.some(actionNeedsAttention);
  return (
    <div className="tab-panel action-workflow-panel">
      <ApprovalTransitionControls {...props} />
      <section className="side-section">
        <SectionHeader title={copy.sections.actions} status={`${rows.length} ${copy.sections.actions}`} tone={hasOpenAction ? UI_TONE.Warn : UI_TONE.Ok} />
        <div className="action-card-grid">
          {rows.map((row) => <ActionCard copy={copy} key={row.id} row={row} busy={state.busy} onInvoke={actions.invokeOpsAction} />)}
        </div>
      </section>
      <ApprovalQueue detail={detail} copy={copy} />
      <OwnerEscalationPanel detail={detail} copy={copy} />
      <CodeBridgePanel state={state} detail={detail} actions={actions} copy={copy} locale={locale} />
    </div>
  );
}

function ApprovalTransitionControls({ detail, state, actions, copy }: IncidentSectionProps): ReactNode {
  const rows = approvalTransitionRows(detail, copy);
  if (!rows.length) {
    return null;
  }
  return (
    <section className="side-section" aria-label={copy.sections.approvalQueue}>
      <SectionHeader title={copy.sections.approvalQueue} status={String(rows.length)} tone={UI_TONE.Warn} />
      <div className="communication-transition-actions">
        {rows.map((row) => (
          <button className={row.primary ? "primary-button" : "secondary-button"} disabled={state.busy} key={row.transition} type={BUTTON_TYPE.Button} onClick={() => void actions.transitionApproval(row.transition)}>
            {state.busy ? copy.auth.working : row.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function CommsPanel(props: IncidentSectionProps): ReactNode {
  const { detail, copy } = props;
  const receiptRows = resolutionNotificationRows(detail, copy);
  const evidenceRows = communicationEvidenceRows(detail, copy);
  const visibleReceiptCount = Math.min(receiptRows.length, CUSTOMER_DRILLDOWN_LIMIT);
  return (
    <div className="tab-panel">
      <CommunicationTransitionControls {...props} />
      <CommunicationEvidenceGrid copy={copy} rows={evidenceRows} />
      <ListPanel title={copy.sections.customerImpactComms} rows={communicationSummaryRows(detail, copy)} />
      <ListPanel title={copy.sections.notifications} rows={notificationRows(detail, copy)} />
      <ListPanel
        title={copy.sections.resolutionNotifications}
        rows={receiptRows}
        maxRows={CUSTOMER_DRILLDOWN_LIMIT}
        overflowLabel={customerVisibleLabel(copy, visibleReceiptCount, receiptRows.length)}
      />
    </div>
  );
}

function CommunicationTransitionControls({ detail, state, actions, copy }: IncidentSectionProps): ReactNode {
  const rows = communicationTransitionRows(detail, copy);
  if (!rows.length) {
    return null;
  }
  return (
    <section className="side-section" aria-label={copy.sections.communicationEvidence}>
      <SectionHeader title={copy.sections.communicationEvidence} status={String(rows.length)} tone={detail.communicationSummary.deliveryRiskCount > 0 ? UI_TONE.Warn : UI_TONE.Ok} />
      <div className="communication-transition-actions">
        {rows.map((row) => (
          <button className={row.primary ? "primary-button" : "secondary-button"} disabled={state.busy} key={row.transition} type={BUTTON_TYPE.Button} onClick={() => void actions.transitionCommunication(row.transition)}>
            {state.busy ? copy.auth.working : row.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function CommunicationEvidenceGrid({ copy, rows }: { readonly copy: OpsCenterCopy; readonly rows: ReturnType<typeof communicationEvidenceRows> }): ReactNode {
  return (
    <section className="communication-evidence-grid" aria-label={copy.sections.communicationEvidence}>
      {rows.map((row) => (
        <article className={`communication-evidence-card ${row.tone}`} key={row.id}>
          <div className="communication-evidence-card-top">
            <h3>{row.title}</h3>
            <StatusPill label={row.state} tone={row.tone} />
          </div>
          <ListRows rows={row.rows} />
        </article>
      ))}
    </section>
  );
}

function approvalTransitionRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly {
  readonly transition: IncidentApprovalTransition;
  readonly label: string;
  readonly primary: boolean;
}[] {
  const rows: {
    readonly transition: IncidentApprovalTransition;
    readonly label: string;
    readonly primary: boolean;
  }[] = [];
  const openDecisions = detail.decisions.filter((item) => !WORKFLOW_CLEAR_STATES.has(item.state) && !WORKFLOW_CLEAR_STATES.has(item.approvalState));
  const approvalActions = actionRows(detail, copy).filter((row) => row.requiresApproval && !WORKFLOW_CLEAR_STATES.has(row.approvalState));
  if (approvalActions.length && !openDecisions.length) {
    rows.push({ transition: IncidentApprovalTransition.RequestApproval, label: copy.actions.requestApproval, primary: false });
  }
  if (openDecisions.length) {
    rows.push({ transition: IncidentApprovalTransition.Approve, label: copy.actions.clearApprovals, primary: true });
    rows.push({ transition: IncidentApprovalTransition.Reject, label: copy.actions.rejectApproval, primary: false });
    rows.push({ transition: IncidentApprovalTransition.Reassign, label: copy.actions.reassignApproval, primary: false });
  }
  if (!detail.communicationSummary.ownerAcknowledged) {
    rows.push({ transition: IncidentApprovalTransition.OwnerAcknowledge, label: copy.actions.acknowledgeOwner, primary: true });
    rows.push({ transition: IncidentApprovalTransition.EscalateOwner, label: copy.actions.escalateOwner, primary: false });
  }
  return rows;
}

function communicationTransitionRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly {
  readonly transition: IncidentCommunicationTransition;
  readonly label: string;
  readonly primary: boolean;
}[] {
  const summary = detail.communicationSummary;
  const rows: {
    readonly transition: IncidentCommunicationTransition;
    readonly label: string;
    readonly primary: boolean;
  }[] = [];
  if (summary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft) {
    rows.push({ transition: IncidentCommunicationTransition.PostOutageNotice, label: copy.actions.postOutageNotice, primary: true });
  }
  if (notificationNeedsTransition(summary.customerNotificationState)) {
    rows.push({ transition: IncidentCommunicationTransition.SendCustomerNotification, label: copy.actions.sendCustomerNotice, primary: true });
  }
  if (communicationCanRecordDeliveryState(summary.customerNotificationState, summary.resolutionNotificationState, summary.deliveryRiskCount)) {
    rows.push({ transition: IncidentCommunicationTransition.MarkDeliveryFailed, label: copy.actions.recordDeliveryFailure, primary: false });
    rows.push({ transition: IncidentCommunicationTransition.HoldDelivery, label: copy.actions.holdDelivery, primary: false });
  }
  if (notificationNeedsTransition(summary.resolutionNotificationState) || summary.deliveryRiskCount > 0) {
    rows.push({ transition: IncidentCommunicationTransition.SendResolutionNotification, label: copy.actions.sendResolutionNotice, primary: true });
  }
  return rows;
}

function notificationNeedsTransition(state: string): boolean {
  return state !== IncidentNotificationState.Sent && state !== IncidentNotificationState.NotRequired;
}

function communicationCanRecordDeliveryState(customerState: string, resolutionState: string, deliveryRiskCount: number): boolean {
  if (deliveryRiskCount > 0) {
    return true;
  }
  return notificationNeedsTransition(customerState) || notificationNeedsTransition(resolutionState);
}

function ResolutionPanel({ state, detail, copy }: IncidentSectionProps): ReactNode {
  const learning = learningRows(detail, copy);
  const proposals = capabilityProposalRows(detail, copy);
  return (
    <div className="tab-panel">
      <ListPanel title={copy.sections.resolutionEvidence} rows={resolutionRows(detail, state.rootCause, copy)} />
      {learning.length ? <ListPanel title={copy.sections.learningRecords} rows={learning} /> : null}
      {proposals.length ? <ListPanel title={copy.sections.capabilityProposals} rows={proposals} /> : null}
    </div>
  );
}

function StandbyPanel({ state, actions, copy, locale }: OpsSectionProps): ReactNode {
  const tabPanel = copy.standby.tabPanels[state.activeTab];
  return (
    <div className="empty-state ops-standby">
      <div className="standby-intro">
        <p className="eyebrow">{copy.standby.introEyebrow}</p>
        <h2>{copy.standby.introTitle}</h2>
        <p className="muted">{copy.standby.introBody}</p>
      </div>
      <dl className="metric-grid standby-metrics">
        <Metric label={copy.standby.api} value={state.health?.ok ? copy.status.healthy : copy.status.unverified} />
        <Metric label={copy.standby.auth} value={isMicrosoftEntraSession(state.session) ? "Microsoft Entra" : authProviderLabel(state, copy)} />
        <Metric label={copy.standby.fixtureMode} value={previewFixtureMode(state.health)} />
        <Metric label={copy.standby.targetSla} value={copy.standby.targetSlaValue} />
      </dl>
      <div className="incident-view-anchor" id={INCIDENT_VIEW_ANCHOR_ID}>
        <OpsCommandBar state={state} actions={actions} copy={copy} locale={locale} />
        <section className="standby-tab-panel incident-view-panel" id={INCIDENT_VIEW_PANEL_ID} role="tabpanel" aria-label={copy.tabs[state.activeTab]}>
          <SectionHeader title={copy.tabs[state.activeTab]} status={state.incident ? copy.sections.active : copy.status.pending} tone={UI_TONE.Neutral} />
          <p className="section-intro">{tabPanel.summary}</p>
          {state.activeTab === TAB_ID.Impact ? <StandbyImpactGraph copy={copy} /> : null}
          <div className="standby-tab-grid">
            {tabPanel.rows.map(([label, summary, statusKey]) => (
              <article className="standby-tab-card" key={label}>
                <div className="standby-tab-card-top">
                  <strong>{label}</strong>
                  <StatusPill label={statusLabel(copy, statusKey)} tone={standbyStatusTone(statusKey)} />
                </div>
                <p>{summary}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function StandbyImpactGraph({ copy }: { readonly copy: OpsCenterCopy }): ReactNode {
  const nodes = [
    [copy.labels.previewIncident, copy.queue.body, 1, 1, GRAPH_HEALTH.Degraded],
    [copy.labels.previewService, copy.standby.readiness.graph[1], 2, 1, GRAPH_HEALTH.Down],
    [copy.labels.previewCustomers, copy.standby.panels.notifications[1], 3, 1, GRAPH_HEALTH.Degraded],
    [copy.labels.previewTools, copy.standby.readiness.policy[1], 1, 2, GRAPH_HEALTH.Unknown],
    [copy.labels.previewOwner, copy.standby.panels.approvals[1], 2, 2, GRAPH_HEALTH.Unknown],
    [copy.labels.previewCodeBridge, copy.standby.panels.codeBridge[1], 3, 2, GRAPH_HEALTH.Unknown]
  ] as const;
  const edges = [
    [copy.labels.previewIncident, copy.labels.previewService],
    [copy.labels.previewService, copy.labels.previewCustomers],
    [copy.labels.previewService, copy.labels.previewOwner],
    [copy.labels.previewTools, copy.labels.previewService],
    [copy.labels.previewCodeBridge, copy.labels.previewService]
  ] as const;
  return (
    <section className="impact-graph standby-impact-graph" aria-label={copy.labels.connectionPoints}>
      <div className="graph-header">
        <SectionHeader title={copy.labels.connectionPoints} status={copy.status.ready} tone={UI_TONE.Ok} />
      </div>
      <div className="graph-canvas standby-preview">
        {nodes.map(([label, summary, column, row, health]) => (
          <article className={`graph-node ${health}`} key={label} style={{ gridColumn: column, gridRow: row } as CSSProperties}>
            <strong>{label}</strong>
            <span>{summary}</span>
          </article>
        ))}
      </div>
      <div className="graph-edges">
        {edges.map(([from, to]) => (
          <article className="graph-edge" key={`${from}:${to}`}>
            <strong>{from}</strong>
            <span>{to}</span>
          </article>
        ))}
      </div>
    </section>
  );
}

function AttentionRail(props: OpsSectionProps): ReactNode {
  const { state, copy } = props;
  return (
    <aside className="attention-rail">
      <SectionHeader title={copy.sections.attentionRequired} />
      {state.incident ? <ActiveAttentionPanels {...props} detail={state.incident} /> : null}
    </aside>
  );
}

function ActiveAttentionPanels(props: IncidentSectionProps): ReactNode {
  const { detail, copy } = props;
  const codeBridgeActions = codeBridgeActionRows(detail, copy);
  const approvalRows = detail.decisions.length + detail.ownerEscalations.length;
  const notificationRows = notificationAttentionRows(detail);
  const actionItems = attentionActionRows(detail, copy);
  return (
    <>
      {hasSlaAttention(detail) ? <SlaPanel detail={detail} copy={copy} /> : null}
      {hasCustomerCommsAttention(detail) ? <CustomerCommsRail actions={props.actions} detail={detail} copy={copy} /> : null}
      {actionItems.length || approvalRows || codeBridgeActions.length ? (
        <RecoveryAttentionSummary
          actionItems={actionItems}
          approvalCount={approvalRows}
          codeBridgeCount={codeBridgeActions.length}
          copy={copy}
          onOpenActions={() => props.actions.setActiveTab(TAB_ID.Actions)}
        />
      ) : null}
      {notificationRows.length ? <NotificationAttentionSummary count={notificationRows.length} copy={copy} onOpenComms={() => props.actions.setActiveTab(TAB_ID.Comms)} /> : null}
    </>
  );
}
function hasSlaAttention(detail: IncidentDetail): boolean {
  const seconds = slaSeconds(detail);
  return seconds.remaining < 0 || seconds.remaining <= 10 * 60;
}

function hasCustomerCommsAttention(detail: IncidentDetail): boolean {
  const summary = detail.communicationSummary;
  return summary.complaintCount > 0
    || summary.deliveryRiskCount > 0
    || notificationStateNeedsAttention(summary.outageNoticeState)
    || notificationStateNeedsAttention(summary.customerNotificationState)
    || notificationStateNeedsAttention(summary.resolutionNotificationState);
}

function notificationAttentionRows(detail: IncidentDetail) {
  return detail.notifications.filter((item) => notificationStateNeedsAttention(item.state));
}

function notificationStateNeedsAttention(state: string): boolean {
  return state !== IncidentNotificationState.NotRequired
    && state !== IncidentNotificationState.Sent
    && state !== OUTAGE_NOTICE_STATE.Posted;
}

function attentionActionRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly ActionRow[] {
  return actionRows(detail, copy).filter((row) => row.endpointKind !== OPS_ACTION_ENDPOINT_KIND.CodeBridgeSession && (row.requiresApproval || row.status === WORKFLOW_STATUS.Blocked));
}

function codeBridgeActionRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly ActionRow[] {
  return actionRows(detail, copy).filter((row) => row.endpointKind === OPS_ACTION_ENDPOINT_KIND.CodeBridgeSession);
}

function attentionOwner(detail: IncidentDetail, copy: OpsCenterCopy): string {
  const ownerRef = detail.ownerEscalations[0]?.recipientRef ?? detail.incident.accountability.currentAssigneeRef;
  return actorRefLabel(ownerRef, copy);
}
function CustomerCommsRail({
  detail,
  copy,
  actions
}: {
  readonly detail: IncidentDetail;
  readonly copy: OpsCenterCopy;
  readonly actions: OpsCenterActions;
}): ReactNode {
  const summary = detail.communicationSummary;
  const tone = summary.deliveryRiskCount > 0 || summary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft ? UI_TONE.Warn : UI_TONE.Ok;
  return (
    <section className="side-section">
      <SectionHeader title={copy.sections.customerImpactComms} status={`${summary.impactedCustomerCount} ${copy.labels.impacted}`} tone={tone} />
      <ListRows rows={[
        [copy.labels.owner, attentionOwner(detail, copy)] as const,
        [copy.labels.actionState, `${copy.labels.outageNotice}: ${statusLabel(copy, summary.outageNoticeState)} / ${copy.labels.resolution}: ${statusLabel(copy, summary.resolutionNotificationState)}`] as const,
        [copy.labels.consequence, summary.deliveryRiskCount > 0 ? copy.labels.deliveryRisk : copy.labels.customerCommsRisk] as const,
        [copy.labels.complaints, String(summary.complaintCount)] as const,
        [copy.labels.deliveryRisks, String(summary.deliveryRiskCount)] as const
      ]} />
      <button className="secondary-button compact-action" type={BUTTON_TYPE.Button} onClick={() => actions.setActiveTab(TAB_ID.Comms)}>
        {copy.tabs[TAB_ID.Comms]}
      </button>
    </section>
  );
}

function SlaPanel({ detail, copy }: { readonly detail: IncidentDetail; readonly copy: OpsCenterCopy }): ReactNode {
  const seconds = slaSeconds(detail);
  return (
    <section className="side-section">
      <SectionHeader title={copy.sections.slaTimer} status={seconds.remaining < 0 ? copy.status.breached : copy.status.open} tone={seconds.remaining < 0 ? UI_TONE.Critical : UI_TONE.Warn} />
      <dl className="metric-grid compact-metrics">
        <Metric label={copy.labels.elapsed} value={`${Math.floor(seconds.elapsed / 60)} min`} />
        <Metric label={copy.labels.remaining} value={seconds.remaining < 0 ? copy.status.breached : `${Math.ceil(seconds.remaining / 60)} min`} />
      </dl>
      <ListRows rows={[
        [copy.labels.owner, attentionOwner(detail, copy)] as const,
        [copy.labels.consequence, copy.labels.slaBreachRisk] as const
      ]} />
    </section>
  );
}

function ActionRail({ detail, actions, copy, state }: IncidentSectionProps): ReactNode {
  const rows = attentionActionRows(detail, copy);
  if (!rows.length) {
    return null;
  }
  return (
    <section className="side-section">
      <SectionHeader title={copy.sections.actionRail} status={`${rows.length} ${copy.sections.actions}`} tone={UI_TONE.Warn} />
      {rows.map((row) => <ActionCard copy={copy} key={row.id} row={row} busy={state.busy} onInvoke={actions.invokeOpsAction} />)}
    </section>
  );
}

function ActionCard({ copy, row, busy, onInvoke }: { readonly copy: OpsCenterCopy; readonly row: ActionRow; readonly busy: boolean; readonly onInvoke: (actionId: string) => Promise<void> }): ReactNode {
  return (
    <article className="action-card">
      <div className="action-card-top">
        <strong>{row.label}</strong>
        <StatusPill label={humanize(row.approvalState)} tone={row.tone} />
      </div>
      <p>{row.summary}</p>
      <ListRows rows={[
        [copy.labels.owner, row.owner] as const,
        [copy.labels.actionState, statusLabel(copy, row.status)] as const,
        [copy.labels.consequence, row.policyLabel] as const,
        [copy.labels.approvalState, statusLabel(copy, row.approvalState)] as const,
        [copy.labels.nextStep, row.nextStep] as const
      ]} />
      <button className="secondary-button" disabled={busy} type={BUTTON_TYPE.Button} onClick={() => void onInvoke(row.id)}>
        {row.buttonLabel}
      </button>
    </article>
  );
}

function ApprovalQueue({ detail, copy }: { readonly detail: IncidentDetail; readonly copy: OpsCenterCopy }): ReactNode {
  const rows = [
    ...detail.decisions.map((item) => [
      item.title,
      `${copy.labels.owner}: ${humanize(item.assignedRole)} / ${copy.labels.actionState}: ${statusLabel(copy, item.state)} / ${copy.labels.consequence}: ${humanize(item.policyClass)}${item.dueAt ? ` / ${copy.labels.due}: ${formatTime(item.dueAt)}` : ""}`
    ] as const),
    ...detail.ownerEscalations.map((item) => [
      copy.sections.ownerEscalation,
      `${copy.labels.owner}: ${actorRefLabel(item.recipientRef, copy)} / ${copy.labels.actionState}: ${statusLabel(copy, item.state)} / ${copy.labels.consequence}: ${humanize(item.reason)} / ${copy.labels.due}: ${formatTime(item.dueAt)}`
    ] as const)
  ];
  return <ListPanel title={copy.sections.approvalQueue} rows={rows} />;
}

function CodeBridgePanel({ detail, copy }: IncidentSectionProps): ReactNode {
  const rows = codeBridgeActionRows(detail, copy);
  return <ListPanel title={copy.sections.codeBridge} rows={rows.map((row) => [row.actionRef, `${copy.labels.actionState}: ${statusLabel(copy, row.status)} / ${copy.labels.consequence}: ${copy.labels.permanentFixRisk}`] as const)} />;
}

function RecoveryAttentionSummary({
  copy,
  actionItems,
  approvalCount,
  codeBridgeCount,
  onOpenActions
}: {
  readonly copy: OpsCenterCopy;
  readonly actionItems: readonly ActionRow[];
  readonly approvalCount: number;
  readonly codeBridgeCount: number;
  readonly onOpenActions: () => void;
}): ReactNode {
  return (
    <section className="side-section">
      <SectionHeader title={copy.sections.actionRail} status={String(approvalCount + actionItems.length + codeBridgeCount)} tone={UI_TONE.Warn} />
      <ListRows rows={[
        [copy.labels.nextStep, actionItems[0]?.label ?? copy.labels.pending] as const,
        [copy.sections.approvalQueue, String(approvalCount)] as const,
        [copy.sections.actions, String(actionItems.length)] as const,
        [copy.sections.codeBridge, String(codeBridgeCount)] as const
      ]} />
      <button className="secondary-button compact-action" type={BUTTON_TYPE.Button} onClick={onOpenActions}>
        {copy.tabs[TAB_ID.Actions]}
      </button>
    </section>
  );
}

function NotificationAttentionSummary({
  copy,
  count,
  onOpenComms
}: {
  readonly copy: OpsCenterCopy;
  readonly count: number;
  readonly onOpenComms: () => void;
}): ReactNode {
  return (
    <section className="side-section">
      <SectionHeader title={copy.sections.notifications} status={String(count)} tone={UI_TONE.Warn} />
      <ListRows rows={[
        [copy.labels.actionState, copy.labels.customerCommsRisk] as const,
        [copy.labels.consequence, copy.labels.deliveryRisk] as const
      ]} />
      <button className="secondary-button compact-action" type={BUTTON_TYPE.Button} onClick={onOpenComms}>
        {copy.tabs[TAB_ID.Comms]}
      </button>
    </section>
  );
}

function NotificationPanel({ copy, rows }: { readonly copy: OpsCenterCopy; readonly rows: ReturnType<typeof notificationAttentionRows> }): ReactNode {
  return <ListPanel title={copy.sections.notifications} rows={rows.map((item) => [humanize(item.template), `${copy.labels.audiences}: ${humanize(item.audienceRef)} / ${copy.labels.actionState}: ${statusLabel(copy, item.state)} / ${copy.labels.consequence}: ${humanize(item.approvalState)}`] as const)} />;
}

function OwnerEscalationPanel({ detail, copy }: { readonly detail: IncidentDetail; readonly copy: OpsCenterCopy }): ReactNode {
  const rows = detail.ownerEscalations.map((item) => [
    actorRefLabel(item.recipientRef, copy),
    `${copy.labels.actionState}: ${statusLabel(copy, item.state)} / ${copy.labels.consequence}: ${humanize(item.reason)} / ${copy.labels.due}: ${formatTime(item.dueAt)}`
  ] as const);
  return <ListPanel title={copy.sections.ownerEscalation} rows={rows} />;
}

function CloseoutPanel({ detail, copy }: { readonly detail: IncidentDetail; readonly copy: OpsCenterCopy }): ReactNode {
  return (
    <section className="side-section">
      <SectionHeader title={copy.sections.closeout} status={statusLabel(copy, detail.incident.status)} tone={statusTone(detail.incident.status)} />
      <ListRows rows={resolutionRows(detail, copy.labels.pending, copy)} />
    </section>
  );
}

function ImpactGraph({ state, detail, actions, copy }: IncidentSectionProps): ReactNode {
  const graph = impactGraph(detail, copy);
  const focusId = state.graphFocusId || graph.nodes[1]?.id || "";
  const focusRows = graphFocusRows(graph, focusId, copy);
  const relationshipHealth = graphRelationshipHealth(graph.edges);
  const activeRelationship = graph.edges.find((edge) => edge.from === focusId || edge.to === focusId) ?? graph.edges[0];
  const relationshipPreviewRows = graph.edges.slice(0, 6);
  return (
    <section className="impact-graph">
      <div className="graph-header">
        <SectionHeader title={copy.sections.impactGraph} />
      </div>
      <div className="graph-canvas incident">
        {graph.nodes.map((node) => (
          <button
            className={`graph-node ${node.kind} ${node.health}${node.id === focusId ? " active" : ""}`}
            key={node.id}
            style={{ gridColumn: node.column, gridRow: node.row } as CSSProperties}
            type={BUTTON_TYPE.Button}
            onClick={() => actions.setGraphFocusId(node.id)}
          >
            <small>{node.roleLabel}</small>
            <strong>{node.label}</strong>
            <span>{node.summary}</span>
            {node.badge ? <em>{node.badge}</em> : null}
          </button>
        ))}
      </div>
      {graph.edges.length ? (
        <div className="graph-relationship-summary" aria-label={copy.labels.topologyPath}>
          <button
            aria-label={`${copy.labels.topologyPath}: ${graph.edges.length} ${copy.labels.connectionPoints}. ${statusLabel(copy, relationshipHealth)}`}
            className={`graph-relationship-summary-button ${relationshipHealth}`}
            title={graph.edges.map((edge) => `${edge.displayLabel}: ${edge.summary}`).join(" | ")}
            type={BUTTON_TYPE.Button}
            onClick={() => actions.setGraphFocusId(relationshipFocusTarget(activeRelationship, focusId))}
          >
            <span className="graph-relationship-glyph" aria-hidden={ARIA_HIDDEN}>
              <svg viewBox="0 0 24 24">
                <circle cx="6" cy="12" r="2.5" />
                <circle cx="18" cy="7" r="2.5" />
                <circle cx="18" cy="17" r="2.5" />
                <path d="M8.4 11.3 15.7 7.8" />
                <path d="M8.4 12.7 15.7 16.2" />
              </svg>
            </span>
            <span>{copy.labels.topologyPath}</span>
            <em>{graph.edges.length}</em>
            <span className="graph-relationship-tooltip" role="tooltip">
              {relationshipPreviewRows.map((edge) => (
                <span className="graph-relationship-tooltip-row" key={edge.id}>
                  <strong>{edge.displayLabel}</strong>
                  <span>{edge.summary}</span>
                  <em>{statusLabel(copy, edge.health)}</em>
                </span>
              ))}
              {graph.edges.length > relationshipPreviewRows.length ? <span className="graph-relationship-more">+{graph.edges.length - relationshipPreviewRows.length}</span> : null}
            </span>
          </button>
        </div>
      ) : null}
      {focusRows.length ? (
        <section className="graph-drill-in">
          <SectionHeader title={copy.labels.connectionPoints} status={String(focusRows.length)} />
          <ListRows rows={focusRows} />
        </section>
      ) : null}
    </section>
  );
}

function graphRelationshipHealth(edges: readonly GraphEdge[]): GraphEdge["health"] {
  if (edges.some((edge) => edge.health === GRAPH_HEALTH.Down)) {
    return GRAPH_HEALTH.Down;
  }
  if (edges.some((edge) => edge.health === GRAPH_HEALTH.Degraded)) {
    return GRAPH_HEALTH.Degraded;
  }
  if (edges.some((edge) => edge.health === GRAPH_HEALTH.Healthy)) {
    return GRAPH_HEALTH.Healthy;
  }
  return GRAPH_HEALTH.Unknown;
}

function relationshipFocusTarget(edge: GraphEdge | undefined, focusId: string): string {
  if (!edge) {
    return focusId;
  }
  if (edge.from === focusId) {
    return edge.to;
  }
  if (edge.to === focusId) {
    return edge.from;
  }
  return edge.to;
}

function actionNeedsAttention(row: ActionRow): boolean {
  return row.status === WORKFLOW_STATUS.Blocked || (row.requiresApproval && !WORKFLOW_CLEAR_STATES.has(row.approvalState));
}

function appEnvironmentRows(apps: readonly CatalogOptionTuple[], environmentsByApp: Readonly<Record<string, readonly CatalogOptionTuple[]>>, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return apps.map(([appId, label]) => [label, optionLabelList(optionsForApp(environmentsByApp, appId), copy)] as const);
}

function ownerCatalogRows(options: readonly CatalogOptionTuple[], copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return ownerDisplayOptions(options, copy).map(([value, label]) => [label, value] as const);
}

function optionValueRows(options: readonly CatalogOptionTuple[]): readonly (readonly [string, string])[] {
  return options.map(([value, label]) => [label, value] as const);
}

function aiAssignmentContextRows(copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [
    [copy.labels.contextSource, copy.labels.incidentLedger],
    [copy.labels.assignmentScope, copy.labels.assignedIncident],
    [copy.labels.redaction, copy.labels.redactedFields]
  ] as const;
}

function optionLabelList(options: readonly CatalogOptionTuple[], copy: OpsCenterCopy): string {
  return options.length ? options.map(([, label]) => label).join(", ") : copy.empty.noneRecorded;
}

function ListPanel({ title, rows, maxRows, overflowLabel }: { readonly title: string; readonly rows: readonly (readonly [string, string])[]; readonly maxRows?: number; readonly overflowLabel?: string }): ReactNode {
  if (!rows.length) {
    return null;
  }
  const visibleRows = maxRows ? rows.slice(0, maxRows) : rows;
  return (
    <section className="side-section list-panel">
      <SectionHeader title={title} status={String(rows.length)} />
      <ListRows rows={visibleRows} />
      {visibleRows.length < rows.length && overflowLabel ? <p className="list-overflow-note">{overflowLabel}</p> : null}
    </section>
  );
}

function ListRows({ rows }: { readonly rows: readonly (readonly [string, string])[] }): ReactNode {
  return (
    <div className="list-rows">
      {rows.map(([label, value], index) => (
        <div className="list-row" key={`${label}-${index}`}>
          <strong>{label}</strong>
          <span>{value}</span>
        </div>
      ))}
    </div>
  );
}

function currentBlocker(detail: IncidentDetail, copy: OpsCenterCopy): string {
  if (detail.communicationSummary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft) {
    return copy.labels.outageNoticeApproval;
  }
  if (detail.communicationSummary.deliveryRiskCount > 0) {
    return copy.labels.deliveryRisk;
  }
  if (actionRows(detail, copy).some((row) => row.requiresApproval)) {
    return copy.labels.approvalRequired;
  }
  return copy.labels.noActiveBlockers;
}

function standbyStatusTone(statusKey: string): UiTone {
  if (statusKey === "ready" || statusKey === "clear" || statusKey === "healthy") {
    return UI_TONE.Ok;
  }
  if (statusKey === "guarded" || statusKey === "open") {
    return UI_TONE.Warn;
  }
  return UI_TONE.Neutral;
}

function resolveActorInitials(displayName: string): string {
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return initials || "O";
}

function toolLabel(copy: OpsCenterCopy, toolId: string, fallback: string): string {
  return copy.toolLabels[toolId] ?? humanize(fallback || toolId);
}

function BotGlyph(): ReactNode {
  return (
    <svg aria-hidden={ARIA_HIDDEN} className="bot-glyph" viewBox="0 0 24 24">
      <path d="M12 3v3" />
      <rect height="13" rx="5" width="16" x="4" y="7" />
      <circle cx="9" cy="13" r="1.25" />
      <circle cx="15" cy="13" r="1.25" />
      <path d="M9 17h6" />
    </svg>
  );
}

function buildLoginHref(loginUrl: string): string {
  const target = new URL(loginUrl, window.location.origin);
  target.searchParams.set("returnTo", `${window.location.pathname}${window.location.search}${window.location.hash}` || "/");
  return target.toString();
}

function orchestratorSummary(detail: IncidentDetail | null, copy: OpsCenterCopy): string {
  if (!detail) {
    return copy.empty.selectIncident;
  }
  if (detail.incident.manualFallbackRequired) {
    return copy.orchestrator.manualFallback;
  }
  return copy.orchestrator.ready;
}
